/**
 * Vanilla-DOM presence chip rendered on the visitor's page while a dashboard
 * agent is connected. Two states: `viewing` (subtle) and `interacting`
 * (prominent). The End button is the visitor's kill switch — clicks fire
 * `onEnd`, which `CobrowseController` translates into `liveview:end`.
 */

export type ChipState = 'viewing' | 'interacting';

const ROOT_ID = 'cobrowsing-presence-chip';

let currentRoot: HTMLElement | null = null;
let currentOnEnd: (() => void) | null = null;

export function showPresenceChip(state: ChipState, onEnd: () => void): void {
  currentOnEnd = onEnd;
  if (!currentRoot) {
    currentRoot = buildChip();
    document.body.appendChild(currentRoot);
  }
  applyState(currentRoot, state);
}

export function hidePresenceChip(): void {
  currentRoot?.remove();
  currentRoot = null;
  currentOnEnd = null;
}

function buildChip(): HTMLElement {
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('role', 'status');
  Object.assign(root.style, {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    zIndex: '2147483645',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '999px',
    background: '#ffffff',
    color: '#111827',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    fontSize: '12px',
    lineHeight: '1',
  } satisfies Partial<CSSStyleDeclaration>);

  const dot = document.createElement('span');
  dot.dataset['cobrowseDot'] = '';
  Object.assign(dot.style, {
    width: '8px',
    height: '8px',
    borderRadius: '999px',
  } satisfies Partial<CSSStyleDeclaration>);

  const label = document.createElement('span');
  label.dataset['cobrowseLabel'] = '';
  Object.assign(label.style, {
    fontWeight: '500',
  } satisfies Partial<CSSStyleDeclaration>);

  const end = document.createElement('button');
  end.type = 'button';
  end.dataset['cobrowseEnd'] = '';
  end.textContent = 'End';
  Object.assign(end.style, {
    marginLeft: '4px',
    padding: '4px 10px',
    border: '1px solid #d1d5db',
    background: '#ffffff',
    color: '#111827',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  end.addEventListener('click', () => currentOnEnd?.());

  root.appendChild(dot);
  root.appendChild(label);
  root.appendChild(end);
  return root;
}

function applyState(root: HTMLElement, state: ChipState): void {
  const dot = root.querySelector<HTMLElement>('[data-cobrowse-dot]');
  const label = root.querySelector<HTMLElement>('[data-cobrowse-label]');
  if (!dot || !label) return;

  if (state === 'viewing') {
    dot.style.background = '#10b981';
    dot.style.animation = '';
    label.textContent = 'Agent is viewing your screen';
    root.style.borderColor = '#e5e7eb';
  } else {
    dot.style.background = '#f59e0b';
    label.textContent = 'Agent is interacting with your screen';
    root.style.borderColor = '#fde68a';
  }
}
